import { createContext, useContext, type ReactNode } from 'react';

/**
 * Every piece of text the renderer itself shows (not the publisher's
 * content). English by default; a host translates them by wrapping the tree
 * in `RendererLabelsProvider` -- this app does it once, in `app/app.tsx`,
 * from its `renderer.*` catalog. Kept as plain strings and functions so the
 * library needs no i18n dependency of its own.
 */
export interface RendererLabels {
	contentUnavailable: string;
	video: string;
	brightcoveVideo: string;
	soundcloudAudio: string;
	videoUnsupported: string;
	embedOffline: (platform: string) => string;
	embedOfflineRetry: string;
	embedFailed: (platform: string) => string;
	twitterTimeline: (screenName: string) => string;
}

export const DEFAULT_RENDERER_LABELS: RendererLabels = {
	contentUnavailable: 'Content unavailable',
	video: 'Video',
	brightcoveVideo: 'Brightcove video',
	soundcloudAudio: 'Soundcloud Audio',
	videoUnsupported: 'Sorry, your browser does not support embedded videos.',
	embedOffline: (platform) => `${platform} content isn't available offline`,
	embedOfflineRetry: "It will load automatically once you're back online.",
	embedFailed: (platform) => `Couldn't load this ${platform} content`,
	twitterTimeline: (screenName) => `${screenName} Twitter/X timeline`,
};

const RendererLabelsContext = createContext<RendererLabels>(
	DEFAULT_RENDERER_LABELS,
);

export function RendererLabelsProvider({
	labels,
	children,
}: {
	labels: RendererLabels;
	children: ReactNode;
}) {
	return (
		<RendererLabelsContext.Provider value={labels}>
			{children}
		</RendererLabelsContext.Provider>
	);
}

export function useRendererLabels(): RendererLabels {
	return useContext(RendererLabelsContext);
}
